<template>
	<main class="qr-scanner-page">
		<video ref="video" autoplay playsinline muted class="qr-scanner-video"></video>
		<div class="qr-scanner-frame">
			<span class="txn-qr-corner txn-qr-corner-tl"></span>
			<span class="txn-qr-corner txn-qr-corner-tr"></span>
			<span class="txn-qr-corner txn-qr-corner-bl"></span>
			<span class="txn-qr-corner txn-qr-corner-br"></span>
			<span class="txn-qr-scan-line"></span>
		</div>
		<p class="qr-scanner-status">{{ status }}</p>
	</main>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { BrowserMultiFormatReader } from '@zxing/browser'

const video = ref(null)
const status = ref('Starting camera...')
const query = new URLSearchParams(window.location.search)
const returnOrigin = query.get('returnOrigin') || ''
let reader = null
let controls = null
let busy = false

function isAllowedReturnOrigin(origin) {
	try {
		const parsed = new URL(origin)
		return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && !!parsed.hostname
	} catch (_) {
		return false
	}
}

function stopScanner() {
	controls?.stop?.()
	controls = null
	const stream = video.value?.srcObject
	stream?.getTracks?.().forEach((track) => track.stop())
	if (video.value) video.value.srcObject = null
}

function extractReceiptUrl(content) {
	const candidate = String(content || '').trim().match(/https?:\/\/[^\s]+/i)?.[0] || String(content || '').trim()
	try {
		const url = new URL(candidate.replace(/[),.;]+$/, ''))
		if (url.protocol !== 'https:' || url.hostname.toLowerCase() !== 'mbreciept.cbe.com.et') return ''
		return url.toString()
	} catch (_) {
		return ''
	}
}

function buildReceiverDisplay(result) {
	const formatAmount = (value) => {
		if (value == null || value === '') return ''
		const numeric = Number(String(value).replace(/,/g, '').replace(/[^\d.-]/g, ''))
		return Number.isFinite(numeric) ? numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value)
	}
	const formatDate = (value) => {
		if (!value) return ''
		const date = new Date(value)
		return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
	}
	return [
		result.receiverName ? `Receiver: ${result.receiverName}` : '',
		result.amount ? `Amount: ${formatAmount(result.amount)}` : '',
		result.receiverAccountLast4 ? `Account: ****${result.receiverAccountLast4}` : '',
		result.transactionDate ? `Transaction Date: ${formatDate(result.transactionDate)}` : '',
	].filter(Boolean).join('\n')
}

async function handleQrResult(content) {
	if (busy) return
	busy = true
	stopScanner()
	status.value = 'Fetching CBE payment details...'
	try {
		const receiptUrl = extractReceiptUrl(content)
		if (!receiptUrl) throw new Error('Please scan a valid CBE receipt QR.')
		const response = await fetch('/api/cbe/public/receipt', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ url: receiptUrl }),
		})
		const data = await response.json().catch(() => ({}))
		if (!response.ok) throw new Error(data.message || `Failed to read CBE receipt (${response.status})`)
		const transactionId = String(data.transactionId || data.reference || data.vatReceiptNo || '').trim()
		if (!transactionId) throw new Error('CBE receipt did not include a Transaction ID')
		if (!window.opener || !isAllowedReturnOrigin(returnOrigin)) throw new Error('The POS window is not available.')
		window.opener.postMessage({
			type: 'cbe-qr-result',
			transactionId,
			receiverName: data.receiverName || '',
			receiverAccountLast4: data.receiverAccountLast4 || '',
			amount: data.amount || '',
			transactionDate: data.transactionDate || '',
			receiverDisplay: buildReceiverDisplay(data),
		}, returnOrigin)
		status.value = 'Scan successful.'
		setTimeout(() => window.close(), 50)
	} catch (error) {
		status.value = error?.message || 'CBE lookup failed. Please try again.'
		busy = false
	}
}

async function startScanner() {
	if (!isAllowedReturnOrigin(returnOrigin)) {
		status.value = 'Invalid POS return address.'
		return
	}
	try {
		await nextTick()
		reader = new BrowserMultiFormatReader()
		controls = await reader.decodeFromConstraints(
			{ video: { facingMode: { ideal: 'environment' } }, audio: false },
			video.value,
			(result, error) => {
				if (result?.getText?.()) handleQrResult(result.getText())
				else if (error && error.name !== 'NotFoundException') status.value = 'Unable to read QR code. Try again.'
			},
		)
		status.value = 'Align the CBE receipt QR inside the frame.'
	} catch (error) {
		status.value = error?.name === 'NotAllowedError' ? 'Camera permission was denied.' : 'Camera could not be started.'
	}
}

onMounted(() => {
	startScanner()
})
onUnmounted(() => {
	stopScanner()
})
</script>

<style scoped>
.qr-scanner-page {
	position: fixed;
	inset: 0;
	overflow: hidden;
	background: #000;
	color: #fff;
}

.qr-scanner-video {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.qr-scanner-frame {
	position: absolute;
	top: 50%;
	left: 50%;
	width: min(72vw, 260px);
	height: min(72vw, 260px);
	transform: translate(-50%, -50%);
	border-radius: 18px;
	box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
	pointer-events: none;
}

.txn-qr-corner {
	position: absolute;
	width: 28px;
	height: 28px;
	border: 3px solid #22c55e;
}

.txn-qr-corner-tl {
	top: 0;
	left: 0;
	border-right: 0;
	border-bottom: 0;
	border-top-left-radius: 12px;
}

.txn-qr-corner-tr {
	top: 0;
	right: 0;
	border-left: 0;
	border-bottom: 0;
	border-top-right-radius: 12px;
}

.txn-qr-corner-bl {
	bottom: 0;
	left: 0;
	border-right: 0;
	border-top: 0;
	border-bottom-left-radius: 12px;
}

.txn-qr-corner-br {
	bottom: 0;
	right: 0;
	border-left: 0;
	border-top: 0;
	border-bottom-right-radius: 12px;
}

.txn-qr-scan-line {
	position: absolute;
	left: 12%;
	right: 12%;
	height: 2px;
	background: linear-gradient(90deg, transparent, #4ade80, transparent);
	animation: txn-qr-scan-move 1.8s ease-in-out infinite;
}

.qr-scanner-status {
	position: absolute;
	left: 1rem;
	right: 1rem;
	bottom: 2rem;
	text-align: center;
	font-size: 0.75rem;
	line-height: 1.25rem;
	color: rgba(255, 255, 255, 0.9);
}

@keyframes txn-qr-scan-move {
	0% { top: 12%; opacity: 0.35; }
	50% { opacity: 1; }
	100% { top: 86%; opacity: 0.35; }
}
</style>
